import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ClipboardCheck, Clock, User, MapPin, Calendar,
  CheckCircle, AlertTriangle, BarChart3, FileText, Loader2,
  Edit, Star, Target, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const statusConfig = {
  scheduled: { bg: 'bg-blue-100 text-blue-700', label: 'Scheduled', step: 0 },
  in_progress: { bg: 'bg-yellow-100 text-yellow-700', label: 'In Progress', step: 1 },
  completed: { bg: 'bg-green-100 text-green-700', label: 'Completed', step: 2 },
  overdue: { bg: 'bg-red-100 text-red-700', label: 'Overdue', step: 0 },
};
const typeIcons = {
  'Hand Hygiene': '🖐', 'Environment of Care': '🏥', 'Medication Safety': '💊',
  'Infection Control': '🦠', 'Patient Safety': '🛡', 'Fire Safety': '🔥',
  'Equipment Check': '⚙', 'Documentation': '📋',
};

const AuditDetailPage = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updateForm, setUpdateForm] = useState({});

  useEffect(() => {
    api.get(`/api/audits/${auditId}`)
      .then(setAudit)
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [auditId]);

  const openUpdate = () => {
    setUpdateForm({
      status: audit.status || 'scheduled',
      score: audit.score != null ? String(audit.score) : '',
      findings: audit.findings || '',
      recommendations: audit.recommendations || '',
      completed_date: audit.completed_date ? audit.completed_date.split('T')[0] : '',
    });
    setShowUpdateModal(true);
  };

  const handleUpdate = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...updateForm,
        score: updateForm.score ? parseFloat(updateForm.score) : null,
      };
      const updated = await api.put(`/api/audits/${auditId}`, payload);
      setAudit(updated);
      toast.success('Audit updated');
      setShowUpdateModal(false);
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const quickStatusChange = async (newStatus) => {
    try {
      const payload = { status: newStatus };
      if (newStatus === 'completed') payload.completed_date = new Date().toISOString().split('T')[0];
      const updated = await api.put(`/api/audits/${auditId}`, payload);
      setAudit(updated);
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" /></div>;
  if (!audit) return <div className="text-center py-24 text-gray-400">Audit not found</div>;

  const stat = statusConfig[audit.status] || statusConfig.scheduled;
  const currentStep = stat.step;
  const scoreColor = audit.score >= 90 ? 'text-green-600' : audit.score >= 70 ? 'text-yellow-600' : audit.score >= 0 ? 'text-red-600' : 'text-gray-400';
  const scoreRing = audit.score >= 90 ? 'stroke-green-500' : audit.score >= 70 ? 'stroke-yellow-500' : audit.score >= 0 ? 'stroke-red-500' : 'stroke-gray-200';

  return (
    <div className="space-y-6" data-testid="audit-detail-page">
      <button onClick={() => navigate('/hospital/audits')} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 text-sm transition-colors" data-testid="back-to-audits">
        <ArrowLeft className="w-4 h-4" /> Back to Audits
      </button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${stat.bg}`}>{stat.label}</span>
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{audit.type}</span>
            </div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900" data-testid="audit-title">{audit.title}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{audit.department}</span>
              {audit.assigned_to_name && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{audit.assigned_to_name}</span>}
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(audit.scheduled_date).toLocaleDateString()}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={openUpdate} className="gap-1.5" data-testid="update-audit-btn">
              <Edit className="w-4 h-4" /> Update Results
            </Button>
            {audit.status === 'scheduled' && (
              <Button size="sm" onClick={() => quickStatusChange('in_progress')} className="gap-1.5 btn-primary" data-testid="start-audit-btn">
                <ClipboardCheck className="w-4 h-4" /> Start Audit
              </Button>
            )}
            {audit.status === 'in_progress' && (
              <Button size="sm" onClick={() => quickStatusChange('completed')} className="gap-1.5 bg-green-600 hover:bg-green-700 text-white" data-testid="complete-audit-btn">
                <CheckCircle className="w-4 h-4" /> Mark Complete
              </Button>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="flex items-center justify-between relative">
            <div className="absolute top-5 left-6 right-6 h-0.5 bg-gray-200" />
            <div className="absolute top-5 left-6 h-0.5 bg-gradient-to-r from-[var(--niko-teal)] to-[var(--niko-green)] transition-all" style={{ width: `${(currentStep / 2) * (100 - 8)}%` }} />
            {[
              { label: 'Scheduled', step: 0, icon: Calendar },
              { label: 'In Progress', step: 1, icon: ClipboardCheck },
              { label: 'Completed', step: 2, icon: CheckCircle },
            ].map(s => (
              <div key={s.step} className="flex flex-col items-center z-10 relative">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                  s.step <= currentStep ? 'bg-[var(--niko-teal)] border-[var(--niko-teal)] text-white' : 'bg-white border-gray-200 text-gray-400'
                }`}><s.icon className="w-4.5 h-4.5" /></div>
                <span className={`text-xs mt-1.5 font-medium ${s.step <= currentStep ? 'text-[var(--niko-teal)]' : 'text-gray-400'}`}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content: Score + Details */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Score Card */}
          {audit.score != null && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="audit-score-card">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Audit Score</h2>
              <div className="flex items-center gap-6">
                <div className="relative w-24 h-24">
                  <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" className="stroke-gray-100" />
                    <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" className={scoreRing}
                      strokeDasharray={`${(audit.score / 100) * 264} 264`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-2xl font-bold ${scoreColor}`}>{audit.score}%</span>
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-lg font-semibold ${scoreColor}`}>
                    {audit.score >= 90 ? 'Excellent' : audit.score >= 70 ? 'Satisfactory' : 'Needs Improvement'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {audit.score >= 90 ? 'All standards met. Continue monitoring.' :
                     audit.score >= 70 ? 'Minor deficiencies found. Follow-up recommended.' :
                     'Significant gaps identified. Immediate action required.'}
                  </p>
                  <div className="flex gap-4 mt-3">
                    {[
                      { label: 'Pass', threshold: 90, color: 'bg-green-500' },
                      { label: 'Warn', threshold: 70, color: 'bg-yellow-500' },
                      { label: 'Fail', threshold: 0, color: 'bg-red-500' },
                    ].map(t => (
                      <div key={t.label} className="flex items-center gap-1.5">
                        <div className={`w-2.5 h-2.5 rounded-full ${t.color}`} />
                        <span className="text-xs text-gray-500">{t.label} (&ge;{t.threshold}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          {audit.description && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-2">Description</h2>
              <p className="text-sm text-gray-600 leading-relaxed">{audit.description}</p>
            </div>
          )}

          {/* Findings */}
          {audit.findings && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="audit-findings">
              <h2 className="text-base font-semibold text-gray-900 mb-2">Findings</h2>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{audit.findings}</p>
            </div>
          )}

          {/* Recommendations */}
          {audit.recommendations && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="audit-recommendations">
              <h2 className="text-base font-semibold text-gray-900 mb-2">Recommendations</h2>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{audit.recommendations}</p>
            </div>
          )}

          {/* Empty state for pending audits */}
          {!audit.findings && !audit.recommendations && audit.status !== 'completed' && (
            <div className="bg-white rounded-2xl border border-gray-100 border-dashed p-8 text-center">
              <ClipboardCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No findings recorded yet</p>
              <p className="text-sm text-gray-400 mt-1">Start the audit and record your findings using the "Update Results" button</p>
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Audit Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between"><span className="text-gray-500">Type</span><span className="font-medium text-gray-900">{typeIcons[audit.type] || '📋'} {audit.type}</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-500">Department</span><span className="font-medium text-gray-900">{audit.department}</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-500">Status</span><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stat.bg}`}>{stat.label}</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-500">Scheduled</span><span className="font-medium text-gray-900">{new Date(audit.scheduled_date).toLocaleDateString()}</span></div>
              {audit.completed_date && <div className="flex items-center justify-between"><span className="text-gray-500">Completed</span><span className="font-medium text-gray-900">{new Date(audit.completed_date).toLocaleDateString()}</span></div>}
              {audit.assigned_to_name && <div className="flex items-center justify-between"><span className="text-gray-500">Auditor</span><span className="font-medium text-gray-900">{audit.assigned_to_name}</span></div>}
              {audit.score != null && <div className="flex items-center justify-between"><span className="text-gray-500">Score</span><span className={`font-bold ${scoreColor}`}>{audit.score}%</span></div>}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Activity Timeline</h3>
            <div className="relative">
              <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-100" />
              {[
                { label: 'Audit Created', time: audit.created_at, color: 'bg-blue-500', done: true },
                { label: 'Scheduled', time: audit.scheduled_date, color: 'bg-yellow-500', done: true },
                ...(audit.status === 'in_progress' || audit.status === 'completed' ? [{ label: 'In Progress', time: audit.updated_at, color: 'bg-yellow-500', done: true }] : []),
                ...(audit.status === 'completed' ? [{ label: 'Completed', time: audit.completed_date || audit.updated_at, color: 'bg-green-500', done: true }] : []),
                ...(audit.status !== 'completed' ? [{ label: 'Pending Completion', time: null, color: 'bg-gray-300', done: false }] : []),
              ].map((entry, i) => (
                <div key={i} className="relative pl-8 pb-4 last:pb-0">
                  <div className={`absolute left-1.5 top-0.5 w-3 h-3 rounded-full ${entry.color} ring-2 ring-white`} />
                  <p className="text-sm font-medium text-gray-900">{entry.label}</p>
                  {entry.time && <p className="text-xs text-gray-400">{new Date(entry.time).toLocaleString()}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Update Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowUpdateModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="update-audit-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Update Audit Results</h2>
              <button onClick={() => setShowUpdateModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label>Status</Label>
                <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={updateForm.status} onChange={e => setUpdateForm({...updateForm, status: e.target.value})}>
                  {['scheduled', 'in_progress', 'completed'].map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                </select>
              </div>
              <div><Label>Score (0-100)</Label><Input type="number" min="0" max="100" className="mt-1" value={updateForm.score} onChange={e => setUpdateForm({...updateForm, score: e.target.value})} data-testid="audit-score-input" /></div>
              {updateForm.status === 'completed' && (
                <div><Label>Completion Date</Label><Input type="date" className="mt-1" value={updateForm.completed_date} onChange={e => setUpdateForm({...updateForm, completed_date: e.target.value})} /></div>
              )}
              <div><Label>Findings</Label><Textarea className="mt-1" rows={4} value={updateForm.findings} onChange={e => setUpdateForm({...updateForm, findings: e.target.value})} placeholder="Document findings..." data-testid="audit-findings-input" /></div>
              <div><Label>Recommendations</Label><Textarea className="mt-1" rows={4} value={updateForm.recommendations} onChange={e => setUpdateForm({...updateForm, recommendations: e.target.value})} placeholder="Recommendations..." data-testid="audit-recommendations-input" /></div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowUpdateModal(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleUpdate} disabled={submitting} className="flex-1 btn-primary" data-testid="submit-update-audit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save Results
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditDetailPage;
